import { MinzeElement, Reactive, EventListeners, MinzeAttrs } from 'minze'
import { Locale } from '../../modules/locale'
import { Font } from '../../constants'
import { ConsoleLog } from '../../modules/logging'

Locale.getInstance().registerKeySet('fr', 'creationRecap', {
  contributorsLabel: 'Contributeurs',
  seeContributorsButton: 'Voir les contributeur·rice·s',
  collapseContributorsButton: 'Voir moins',
  modify: 'Modifier',
})

Locale.getInstance().registerKeySet('en', 'creationRecap', {
  contributorsLabel: 'Contributors',
  seeContributorsButton: 'See contributors',
  collapseContributorsButton: 'See less',
  modify: 'Modify',
})

export interface LvcCreationRecap {
  eventData: any
  dataTitle: string
  dataCategory: string
  dataDate: string
  dataAddress: string
  dataPrice: string
  dataText: string
  dataImage: string
  dataDescription: string
  flowGroup: string
  flowStep: string
  dataContributors: any[]
  showContributors: boolean
}

export class LvcCreationRecap extends MinzeElement {
  attrs: MinzeAttrs = [
    ['event-data', ''],
    ['show-modify-button', true],
    ['data-adding-events', false],
  ]

  reactive: Reactive = [
    ['dataImage', ''],
    ['dataTitle', ''],
    ['dataDisciplines', ''],
    ['dataDate', ''],
    ['dataAddress', ''],
    ['dataPrice', ''],
    ['dataDescription', ''],
    ['dataContributors', []],
    ['showContributors', false],
  ]

  static observedAttributes = ['event-data']

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ) {
    if (name === 'event-data' && oldValue !== newValue) {
      this.getEventRecapData()
    }
  }

  getEventRecapData() {
    const data = JSON.parse(decodeURIComponent(this.eventData)) as any
    this.dataImage = data.image || ''
    this.dataTitle = data.eventName || ''
    this.dataDisciplines = this.getEventDisciplinesString(data.eventDisciplines) || ''
    this.dataDescription = data.eventDescription || ''
    this.dataContributors = data.eventContributors || []
    this.rerender()
  }

  getEventDisciplinesString(disciplines: any) {
    if (disciplines) {
      const allDisciplines = JSON.parse(disciplines)
      return allDisciplines.map((d: any) => d.label).join(', ')
    }
    return ''
  }

  onContributorsButtonClick() {
    ConsoleLog(true, 'goToContributors')
    this.showContributors = !this.showContributors
  }

  html = () => `
    <lvc-box class="wrapper" background="white" borderRadius="8px">
      <lvc-box border="1px solid #1E007D4D" borderRadius="8px">
        ${
          this.dataImage
            ? /*html*/ `
        <img class="cover-image" src="${this.dataImage}" alt="" class="image-container" />`
            : /*html*/ `
        <div class="image-loader-container">
          <div class="image-loader"></div>
        </div>
        `
        }
        <lvc-box padding="24px">
          <lvc-box mb="16px" display="flex" flexDirection="column" rowGap="4px">
            ${
              this.dataTitle
                ? `<lvc-text font-preset="largeExtraBold" text-transform="uppercase">${this.dataTitle}</lvc-text>`
                : ''
            }
            ${
              this.dataDisciplines
                ? `<lvc-text font-preset="small" font-size="12px" line-height="18px">${this.dataDisciplines}</lvc-text>`
                : ''
            }
            ${
              this.dataDate
                ? `<lvc-text font-preset="small" font-size="12px" line-height="18px">${this.dataDate}</lvc-text>`
                : ''
            }
            ${
              this.dataAddress
                ? `<lvc-text font-preset="small" font-size="12px" line-height="18px">${this.dataAddress}</lvc-text>`
                : ''
            }
            ${
              this.dataPrice
                ? `
                <lvc-box class="priceContainer">
                  <lvc-text font-size="12px" font-weight="700" line-height="150%" class="priceText"> 
                    ${this.dataPrice}
                  </lvc-text>
                </lvc-box>
              `
                : ''
            }
          </lvc-box>
          ${
            this.dataDescription
              ? // white-space:pre-wrap keeps the paragraph breaks the user typed
                // (PRO-50); the interpolation is kept tight against the tags so the
                // template's own indentation is not rendered as leading whitespace.
                `<lvc-text font-preset="small" line-height="22px" white-space="pre-wrap">${this.dataDescription}</lvc-text>`
              : ''
          }
          ${
            this.dataContributors?.length
              ? `
            <hr class="divider"/>
          `
              : ''
          }
          ${
            this.showContributors
              ? `
            <lvc-box>
              <lvc-text font-preset="medium">
                ${Locale.getKey('creationRecap.contributorsLabel')}
              </lvc-text>
              <lvc-box mt="16px">
                ${this.dataContributors
                  ?.map(
                    contributor => `
                  <lvc-box mb="16px">
                    <lvc-box mb="8px">
                      <lvc-text font-preset="smallExtraBold">
                        ${contributor?.contributorName}
                      </lvc-text>
                    </lvc-box>
                    <lvc-box>
                      <lvc-text font-preset="small">
                        ${contributor?.contributorType} - ${contributor?.contributionType}
                      </lvc-text>
                    </lvc-box>
                  </lvc-box>
                `
                  )
                  .join('')}
              </lvc-box>
            </lvc-box>
          `
              : ''
          }
          ${
            this.dataContributors?.length
              ? `
            <lvc-button theme="transparent" height="40px" border="1px solid #1E007D" name="goToContributors">
              ${Locale.getKey(
                this.showContributors
                  ? 'creationRecap.collapseContributorsButton'
                  : 'creationRecap.seeContributorsButton'
              )}
            </lvc-button>
          `
              : ''
          }
        </lvc-box>
      </lvc-box>
    </lvc-box>
    <lvc-box mt="8px" textAlign="right">
      ${this.showModifyButton ? `<button class="modify">${Locale.getKey('creationRecap.modify')}</button>` : ''}
    </lvc-box>
  `

  css = () => `
    .image-loader-container {
      width: 100%;
      aspect-ratio: 16/9;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .image-loader {
      border: 2px solid #1E007D;
      border-top: 2px solid transparent;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    .wrapper {
      overflow: hidden;
    }
    .divider {
      background: #1E007D;
      height: 1px;
      border: none;
      margin: 16px 0;
      opacity: 0.3;
    }
    .modify {
      color: #1E007D;
      font-weight: ${Font.weight.medium};
      font-size: 12px;
      line-height: 14px;
      text-decoration: underline;
      cursor: pointer;
      border: none;
      background: none;
      padding: 0;
      margin: 0;
    }
    .cover-image {
      aspect-ratio: 16/9;
      object-fit: cover;
      width: calc(100% + 2px);
      position: relative;
      right: 1px;
      bottom: 1px;
      border-radius: 2px;
    }
    .priceText{
      background: #BFF0FF;
      border-radius: 4px;
      padding: 5px 8px;
      width: fit-content;
    }
  `

  eventListeners: EventListeners = [
    ['lvc-button[name="goToContributors"]', 'click', this.onContributorsButtonClick.bind(this)],
  ]
}
